// src/components/Admin/ReportManager/ReportTable.jsx
import React, {
  useState, useRef, useMemo, useEffect,
} from 'react';
import {
  Paper,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Pagination,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import ReportItem from './ReportItem';
import ReportLogModal from './ReportLogModal';
import ReportersModal from './ReportersModal';
import ReportCardItem from './ReportCardItem';

const SORT_OPTIONS = [
  { value: 'date', label: '신고일순' },
  { value: 'count', label: '횟수순' },
  { value: 'reporter', label: '신고자순' },
];
const ROWS_PER_PAGE = 10;

export default function ReportTable({ reports = [], onProcess }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [page, setPage] = useState(1);

  const logModalRef = useRef();
  const reportersModalRef = useRef();

  /* ───── 필터 + 정렬 ───── */
  const filteredAndSorted = useMemo(() => {
    const filtered = reports
      .filter((r) =>
        !search.trim()
        || (r.reason || '').includes(search)
        || (r.target_title || '').includes(search)
        || (r.reporter || '').includes(search)
        || (r.created_at || '').includes(search))
      .filter((r) => (statusFilter === 'all' ? true : r.status === statusFilter));

    return filtered.sort((a, b) => {
      if (sortBy === 'date') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'count') return (b.report_count || 0) - (a.report_count || 0);
      if (sortBy === 'reporter') return (a.reporter || '').localeCompare(b.reporter || '');
      return 0;
    });
  }, [reports, search, statusFilter, sortBy]);

  /* ───── 페이지네이션 ───── */
  const totalPages = Math.ceil(filteredAndSorted.length / ROWS_PER_PAGE) || 1;
  const paginated = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return filteredAndSorted.slice(start, start + ROWS_PER_PAGE);
  }, [filteredAndSorted, page]);

  useEffect(() => { setPage(1); }, [search, statusFilter, sortBy]);

  /* ───── 신고자 모달 핸들러 ───── */
  const handleShowReporters = (type, id) =>
    reportersModalRef.current?.open(type, id);

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: isMobile ? 1.5 : 2,
          width: '100%',
          minWidth: 0,
        }}
      >
        {/* 검색·필터·정렬 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            label="사유/제목/닉네임/날짜"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: isMobile ? '100%' : 260 }}
          />

          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 130 }}>
            <InputLabel>상태</InputLabel>
            <Select
              value={statusFilter}
              label="상태"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="pending">대기</MenuItem>
              <MenuItem value="blinded">블라인드</MenuItem>
              <MenuItem value="deleted">삭제</MenuItem>
              <MenuItem value="ignored">무시</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 150 }}>
            <InputLabel>정렬 기준</InputLabel>
            <Select
              value={sortBy}
              label="정렬 기준"
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* ✅ 모바일: 카드형 */}
        {isMobile ? (
          <Stack spacing={1.25} sx={{ width: '100%', minWidth: 0 }}>
            {paginated.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography sx={{ color: 'text.secondary' }}>
                  🔍 조건에 맞는 신고가 없습니다.
                </Typography>
              </Paper>
            ) : (
              paginated.map((r) => (
                <ReportCardItem
                  key={r.id}
                  report={r}
                  onProcess={onProcess}
                  onLogView={(id) => logModalRef.current?.openModal(id)}
                  onShowReporters={handleShowReporters}
                />
              ))
            )}

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  showFirstButton
                  showLastButton
                  shape="rounded"
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </Stack>
        ) : (
          /* ✅ PC: 테이블 유지 */
          <>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">구분</TableCell>
                    <TableCell>신고 대상</TableCell>
                    <TableCell>신고 사유</TableCell>
                    <TableCell align="center">신고자</TableCell>
                    <TableCell>신고일</TableCell>
                    <TableCell>횟수</TableCell>
                    <TableCell align="center">상태</TableCell>
                    <TableCell align="center">처리자</TableCell>
                    <TableCell align="center" sx={{ width: 140 }}>처리</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginated.map((r) => (
                    <ReportItem
                      key={r.id}
                      report={r}
                      onProcess={onProcess}
                      onLogView={(id) => logModalRef.current?.openModal(id)}
                      onShowReporters={handleShowReporters}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  showFirstButton
                  showLastButton
                  shape="rounded"
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* 모달들 */}
      <ReportLogModal ref={logModalRef} />
      <ReportersModal ref={reportersModalRef} />
    </>
  );
}
